import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart, Megaphone, Unlock, Sparkles, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal, Overline } from "../components/site/Reveal";
import Marquee from "../components/site/Marquee";
import Timeline from "../components/site/Timeline";
import FocalImage from "../components/FocalImage";
import api from "../lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1622352496174-9e1d969b1945?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  founder: "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};
const VALUE_ICONS = [Heart, Megaphone, Unlock, Sparkles];
const CARD_HUES = ["22 60% 52%", "150 40% 34%", "38 62% 52%", "330 38% 55%"];
const DEFAULT_ORDER = ["hero", "values_marquee", "promises", "founder", "timeline", "stats", "press", "cta"];

function TwoTone({ text, className }) {
  const idx = (text || "").indexOf(",");
  if (idx === -1) return <span className={className}>{text}</span>;
  return (
    <span className={className}>
      <span className="text-foreground">{text.slice(0, idx + 1)}</span>{" "}
      <span className="text-secondary italic">{text.slice(idx + 1).trim()}</span>
    </span>
  );
}

export default function Home() {
  const { content, t } = useLang();
  const h = content.home || {};
  const values = content.values || [];
  const timeline = content.timeline || [];
  const stats = content.stats || [];
  const press = content.press_logos || [];

  const [layout, setLayout] = useState(null);
  const [imgs, setImgs] = useState({});
  useEffect(() => {
    api.get("/home-layout").then((r) => setLayout(r.data.sections))
      .catch(() => setLayout(DEFAULT_ORDER.map((id) => ({ id, enabled: true }))));
    api.get("/home-images").then((r) => setImgs(r.data)).catch(() => {});
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const blocks = {
    hero: (
      <section ref={heroRef} className="relative overflow-hidden hero-pad">
        <div className="absolute -top-24 -right-16 w-[38rem] h-[38rem] rounded-full bg-accent/40 blur-3xl blob pointer-events-none" />
        <div className="absolute top-40 -left-24 w-96 h-96 rounded-full bg-secondary/10 blur-3xl blob pointer-events-none" style={{ animationDelay: "-6s" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-8 md:pt-10 pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Overline data-testid="home-hero-overline">{content.brand?.tagline || h.hero_overline}</Overline>
            </motion.div>
            <motion.h1 className="font-display text-[3.25rem] leading-[0.98] md:text-7xl lg:text-[5.5rem] font-semibold tracking-tight mt-4"
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} data-testid="home-hero-title">
              <TwoTone text={h.hero_title || ""} />
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
              className="text-base md:text-lg text-foreground/70 leading-relaxed mt-6 max-w-xl">{h.hero_body}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} className="flex flex-wrap gap-4 mt-8">
              <Link to="/join" data-testid="home-hero-join" className="group rounded-full bg-secondary text-secondary-foreground px-7 py-3.5 font-semibold flex items-center gap-2 hover:bg-primary transition-colors">
                {h.hero_cta} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/about" data-testid="home-hero-story" className="rounded-full border border-primary/25 px-7 py-3.5 font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors">{h.hero_cta2}</Link>
            </motion.div>
            <div className="flex items-center gap-8 mt-10">
              {stats.slice(0, 3).map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.1 }}>
                  <p className="font-display text-3xl font-semibold text-primary">{s.value}</p>
                  <p className="text-xs text-muted-foreground max-w-[7rem] leading-tight">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative">
            <div className="overflow-hidden rounded-[2.25rem] aspect-[4/5] shadow-2xl shadow-primary/10">
              <motion.div style={{ y: imgY, scale: imgScale }} className="w-full h-full">
                <FocalImage img={imgs.hero || { url: IMG.hero }} alt="GYBC community" testid="home-hero-image" />
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="absolute -bottom-6 -left-4 md:-left-8 bg-background/90 backdrop-blur-xl border border-primary/10 rounded-2xl p-5 shadow-xl max-w-[15rem]">
              <Quote size={20} className="text-secondary" />
              <p className="text-sm text-primary/90 mt-1 leading-snug">{t("Someone to have your back changes everything.", "Avoir quelqu'un pour vous soutenir change tout.")}</p>
              <p className="text-xs text-muted-foreground mt-2 font-semibold">— Kelly P., Member</p>
            </motion.div>
            <div className="absolute -top-4 -right-2 bg-secondary text-secondary-foreground rounded-2xl px-4 py-3 shadow-lg rotate-3">
              <p className="font-display text-2xl font-semibold leading-none">500+</p>
              <p className="text-[10px] uppercase tracking-wider">{t("women", "femmes")}</p>
            </div>
          </motion.div>
        </div>
      </section>
    ),
    values_marquee: (
      <section className="bg-primary text-primary-foreground py-5 grain"><Marquee items={values.map((v) => v.title)} /></section>
    ),
    promises: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <Reveal><Overline>{h.values_overline}</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-3 max-w-2xl text-primary">{h.values_title}</h2></Reveal>
          </div>
          <Reveal delay={0.15}><Link to="/about" className="link-sweep text-secondary font-semibold shrink-0">{t("Our promises", "Nos promesses")} →</Link></Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {values.map((v, i) => {
            const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
            const hue = CARD_HUES[i % CARD_HUES.length];
            return (
              <Reveal key={i} delay={i * 0.08}>
                <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="group relative rounded-3xl p-7 h-full overflow-hidden border"
                  style={{ background: `linear-gradient(160deg, hsl(${hue} / 0.14), hsl(${hue} / 0.03) 55%, transparent)`, borderColor: `hsl(${hue} / 0.28)` }}>
                  <div className="absolute -bottom-10 -right-8 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500" style={{ background: `hsl(${hue} / 0.5)` }} />
                  <span className="absolute top-4 right-5 font-display text-6xl font-semibold leading-none" style={{ color: `hsl(${hue} / 0.18)` }}>0{i + 1}</span>
                  <motion.div whileHover={{ rotate: -8, scale: 1.08 }} transition={{ type: "spring", stiffness: 300 }}
                    className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-5 shadow-lg"
                    style={{ background: `hsl(${hue})`, boxShadow: `0 10px 25px -8px hsl(${hue} / 0.6)` }}>
                    <Icon size={24} />
                  </motion.div>
                  <h3 className="relative font-display text-2xl font-semibold text-primary mb-2">{v.title}</h3>
                  <p className="relative text-foreground/70 leading-relaxed text-sm">{v.body}</p>
                  <span className="relative mt-4 inline-block h-1 w-8 rounded-full transition-all duration-300 group-hover:w-14" style={{ background: `hsl(${hue})` }} />
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </section>
    ),
    founder: (
      <section className="bg-card/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative max-w-md">
              <div className="overflow-hidden rounded-[2rem] aspect-[4/5]"><FocalImage img={imgs.founder || { url: IMG.founder }} alt={h.founder_name} testid="home-founder-image" /></div>
              <div className="absolute -bottom-5 right-6 bg-primary text-primary-foreground rounded-2xl px-5 py-3 shadow-xl">
                <p className="font-display text-lg font-semibold">{h.founder_name}</p>
                <p className="text-xs text-primary-foreground/60">{t("Founder", "Fondatrice")}</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><Overline>{h.founder_overline}</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-3 text-primary">{h.founder_name}</h2></Reveal>
            <Reveal delay={0.2}><p className="text-lg text-foreground/70 leading-relaxed mt-5">{h.founder_body}</p></Reveal>
            <Reveal delay={0.3}><Link to="/about" className="inline-flex items-center gap-2 mt-7 rounded-full border border-primary/25 px-6 py-3 font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors">{t("Read her story", "Lire son histoire")} <ArrowRight size={16} /></Link></Reveal>
          </div>
        </div>
      </section>
    ),
    timeline: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><Overline>{t("The journey", "Le parcours")}</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-3 text-primary">{t("From a group text to a movement.", "D'un message de groupe à un mouvement.")}</h2></Reveal>
        </div>
        <Timeline items={timeline} />
      </section>
    ),
    stats: (
      <section className="bg-primary text-primary-foreground py-16 md:py-24 grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <Reveal><p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">{h.stats_overline}</p></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border-l-2 border-secondary pl-5">
                  <p className="font-display text-5xl md:text-6xl font-semibold">{s.value}</p>
                  <p className="text-primary-foreground/60 mt-2 leading-snug text-sm">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    ),
    press: (
      <section className="py-12 overflow-hidden">
        <p className="text-center text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-8">{t("As seen in", "Vu dans")}</p>
        <div className="text-foreground/60"><Marquee items={press} /></div>
      </section>
    ),
    cta: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        <Reveal>
          <div className="relative bg-secondary text-secondary-foreground rounded-[2.5rem] p-10 md:p-16 text-center grain overflow-hidden">
            <div className="absolute -top-16 -left-10 w-72 h-72 rounded-full bg-primary/20 blur-2xl blob" />
            <h2 className="relative font-display text-4xl md:text-6xl font-medium tracking-tight">{t("Ready to find your people?", "Prête à trouver vos gens ?")}</h2>
            <p className="relative text-lg text-secondary-foreground/80 mt-4 max-w-xl mx-auto">{t("Join 500+ women who've got each other's backs.", "Rejoignez plus de 500 femmes qui se soutiennent.")}</p>
            <Link to="/join" data-testid="home-cta-join" className="relative inline-flex items-center gap-2 mt-8 rounded-full bg-primary text-primary-foreground px-8 py-4 font-semibold hover:opacity-90 transition-opacity">{t("Join GYBC", "Rejoindre GYBC")} <ArrowRight size={18} /></Link>
          </div>
        </Reveal>
      </section>
    ),
  };

  const order = layout || DEFAULT_ORDER.map((id) => ({ id, enabled: true }));

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      {order.filter((s) => s.enabled && blocks[s.id]).map((s) => (
        <React.Fragment key={s.id}>{blocks[s.id]}</React.Fragment>
      ))}
    </div>
  );
}
